import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { ActionTile, TileGrid } from "../ui/ActionTile";
import {
  IconApprovals,
  IconHealth,
  IconPayables,
  IconRefunds,
} from "../ui/icons";
import { AuthRequired } from "../components/AuthRequired";
import { ErrorBlock, LoadingBlock, MetricCard, MoneyValue, StatusChip } from "../components/CrmCards";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";
import {
  getTreasurySummary,
  type CompositionBucket,
  type MoneyMinor,
  type TreasurySummaryResponse,
} from "../api/treasuryPayables";

// Treasury Accounting v2 — read-only dashboard. Consumes BE #198
// /v2/admin/bff/treasury/summary (treasuryComposition + partnerAccounting +
// warnings). No funds move, no mutation. Human amount first; raw secondary.

const WARNING_COPY: Record<string, string> = {
  open_blocked_requests_present: "Some payout requests are blocked (missing/invalid destination).",
  escrow_composition_unavailable: "Escrow composition could not be read (RPC/config).",
  estimated_operating_treasury_negative: "Estimated operating treasury is negative — liabilities exceed wallet balance.",
};
function warningCopy(w: string): string {
  if (WARNING_COPY[w]) return WARNING_COPY[w];
  if (w.startsWith("treasury_balance_unavailable")) return `Treasury wallet balance unavailable (${w.split(":")[1] ?? "rpc"}).`;
  return w;
}

/** Render a composition bucket: amount when available, else "Unavailable" +
 *  reason (NOT "Blocked" — these are degraded reads, not a security state). */
function BucketValue({ bucket }: { bucket: CompositionBucket | undefined }) {
  if (bucket && bucket.available) {
    return <MoneyValue display={bucket.displayAmount} raw={bucket.rawAmount} size={20} />;
  }
  return (
    <div style={{ fontSize: 13, color: "var(--fg-dim)" }}>
      Unavailable
      {bucket && !bucket.available && bucket.reason ? (
        <div style={{ fontSize: 11, marginTop: 4 }}>({bucket.reason})</div>
      ) : null}
    </div>
  );
}

/** Render a USD-minor money bucket; falls back to "0.00 USD" rather than blank. */
function MinorValue({ m, size = 20, tone }: { m: MoneyMinor | null | undefined; size?: number; tone?: string }) {
  const display = m?.displayAmount ?? "0.00 USD";
  const raw = m?.rawAmount;
  return <MoneyValue display={display} raw={raw} size={size} tone={tone} />;
}

function TreasuryInner() {
  const [summary, setSummary] = useState<TreasurySummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      setSummary(await getTreasurySummary());
    } catch (e) {
      setError(mapAffiliateError(e).message);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && summary === null) return <LoadingBlock label="Loading treasury…" />;
  if (error) return <ErrorBlock message={error} onRetry={() => void load()} />;
  if (summary === null) return <LoadingBlock label="Loading treasury…" />;

  const comp = summary.treasuryComposition ?? {};
  const acct = summary.partnerAccounting ?? {};
  const disp = acct.display ?? {};
  const counts = acct.counts ?? {};
  const warnings = summary.warnings ?? [];
  const bal = summary.treasuryBalance;
  const estMinor = acct.estimatedOperatingTreasuryMinor;
  const blockedCount = counts.openBlockedRequests ?? 0;
  const estNegative = typeof estMinor === "number" && estMinor < 0;

  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 } as const;
  const sectionTitle = {
    fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6,
    color: "var(--fg-dim)", margin: "18px 0 8px 0",
  } as const;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "4px 0 8px 0" }}>
        <button type="button" onClick={() => void load()} disabled={loading} style={{ padding: "4px 12px", fontSize: 13 }}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
        <span style={{ fontSize: 11, color: "var(--fg-dim)" }}>chain {summary.chainId} · {summary.token}</span>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div
          role="alert"
          style={{
            margin: "8px 0 4px 0", padding: "10px 14px", borderRadius: 8, fontSize: 12.5, lineHeight: 1.5,
            border: `1px solid ${estNegative ? "#d23f3f" : "var(--warn, #b58900)"}`,
            background: estNegative ? "rgba(210,63,63,0.06)" : "rgba(181,137,0,0.08)",
            color: estNegative ? "#d23f3f" : "var(--fg)",
          }}
        >
          <strong>Warnings</strong>
          <ul style={{ margin: "6px 0 0 0", paddingLeft: 18 }}>
            {warnings.map((w) => (
              <li key={w}>
                {warningCopy(w)}
                {w === "open_blocked_requests_present" && (
                  <> <Link to="/admin/partner-payables?tab=blocked" style={{ color: "var(--accent, #2c7be5)" }}>Review</Link></>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Top: treasury + liability snapshot */}
      <div style={sectionTitle}>Snapshot</div>
      <div style={grid}>
        <MetricCard
          title="Treasury wallet balance"
          accent="#0a9444"
          badge={bal.available ? <StatusChip status="ready" /> : undefined}
          footer={summary.treasuryAddressShort ? <span title={summary.treasuryAddress ?? undefined}>wallet <code style={{ fontSize: 11 }}>{summary.treasuryAddressShort}</code></span> : "Treasury wallet not configured."}
        >
          <BucketValue bucket={comp.treasuryWalletBalance ?? (bal as CompositionBucket)} />
        </MetricCard>

        <MetricCard title="Partner matured liability" accent="var(--accent, #2c7be5)" footer="Owed to partners now (matured − paid − legacy). Off-chain ledger liability.">
          <MinorValue m={disp.partnerMaturedLiability ?? summary.partnerPayableReserve} />
        </MetricCard>

        <MetricCard
          title="Estimated operating treasury"
          accent={estNegative ? "#d23f3f" : "#0a9444"}
          badge={estNegative ? <StatusChip status="blocked" /> : undefined}
          footer="≈ wallet balance − partner liability − credit liability (USD≈USDC 1:1)."
        >
          {disp.estimatedOperatingTreasury ? (
            <MinorValue m={disp.estimatedOperatingTreasury} tone={estNegative ? "#d23f3f" : undefined} />
          ) : (
            <div style={{ fontSize: 13, color: "var(--fg-dim)" }}>Unavailable<div style={{ fontSize: 11, marginTop: 4 }}>(treasury balance unavailable)</div></div>
          )}
        </MetricCard>

        <MetricCard title="Credit liability" accent="#b8860b" footer="In-ecosystem credits granted (held), not yet spent.">
          <MinorValue m={disp.creditLiability} />
        </MetricCard>
      </div>

      {/* Escrow composition */}
      <div style={sectionTitle}>Escrow composition (payment / legacy state)</div>
      <div style={grid}>
        <MetricCard title="V2 escrow treasury withdrawable" accent="var(--fg-dim, #888)" footer="Withdrawable to Treasury via legacy escrow.">
          <BucketValue bucket={comp.v2EscrowTreasuryWithdrawable} />
        </MetricCard>
        <MetricCard title="V3 locked deposits" accent="var(--fg-dim, #888)" footer="Funds still locked/deposited in escrow.">
          <BucketValue bucket={comp.v3LockedDeposits} />
        </MetricCard>
        <MetricCard title="Total escrow balance" accent="var(--fg-dim, #888)" footer="Total USDC held by the escrow contract.">
          <BucketValue bucket={comp.escrowTotalBalance} />
        </MetricCard>
      </div>

      {/* Partner accounting */}
      <div style={sectionTitle}>Partner accounting</div>
      <div style={grid}>
        <MetricCard title="Matured payable" accent="var(--accent, #2c7be5)" footer="Matured eligible reward (cash).">
          <MinorValue m={disp.maturedEligible} />
        </MetricCard>
        <MetricCard title="Not-yet-matured rewards" accent="var(--fg-dim, #888)" footer="Earned but not payable until matured.">
          <MinorValue m={disp.notMatured} />
        </MetricCard>
        <MetricCard title="Available to request" accent="#0a9444" footer="Matured liability not covered by a valid open request.">
          <MinorValue m={disp.availableToRequest} />
        </MetricCard>
        <MetricCard title="Open valid payout requests" accent="var(--accent, #2c7be5)" footer={`${counts.openValidRequests ?? 0} request(s) with a valid destination.`}>
          <MinorValue m={disp.openValidRequests} />
        </MetricCard>
        <MetricCard
          title="Blocked payout requests"
          accent={blockedCount > 0 ? "#b8860b" : "var(--fg-dim, #888)"}
          badge={blockedCount > 0 ? <StatusChip status="blocked" /> : undefined}
          footer={blockedCount > 0 ? `${blockedCount} request(s) missing/invalid destination.` : "None."}
          cta={blockedCount > 0 ? { label: "Review blocked", to: "/admin/partner-payables?tab=blocked" } : undefined}
        >
          <MinorValue m={disp.openBlockedRequests} tone={blockedCount > 0 ? "#b8860b" : undefined} />
        </MetricCard>
        <MetricCard title="Paid verified payouts" accent="#0a9444" footer="Payouts with a verified on-chain transfer.">
          <MinorValue m={disp.paidVerified} />
        </MetricCard>
      </div>

      {/* Explanation */}
      <div style={sectionTitle}>How to read this</div>
      <div style={grid}>
        <MetricCard title="Partner rewards are ledger liabilities" accent="var(--fg-dim, #888)">
          <div style={{ fontSize: 12.5, lineHeight: 1.55 }}>
            Partner rewards are <strong>ledger liabilities paid from the Gao Treasury</strong>. They are{" "}
            <strong>not separate on-chain affiliate buckets</strong>. Escrow balances represent
            payment / legacy settlement state, not partner payout balance.
          </div>
        </MetricCard>
        <MetricCard title="Open Partner Payables" accent="var(--accent, #2c7be5)" cta={{ label: "Open Partner Payables", to: "/admin/partner-payables" }}>
          <div style={{ fontSize: 12.5, color: "var(--fg-dim)" }}>Approve + record treasury transfers per partner.</div>
        </MetricCard>
        <MetricCard title="Dashboard" accent="var(--fg-dim, #888)" cta={{ label: "Open Dashboard", to: "/dashboard" }}>
          <div style={{ fontSize: 12.5, color: "var(--fg-dim)" }}>CRM overview.</div>
        </MetricCard>
        <MetricCard title="Advanced — legacy escrow withdraw" accent="var(--fg-dim, #888)" cta={{ label: "Open legacy escrow withdraw", to: "/escrow/withdraw-treasury" }}>
          <div style={{ fontSize: 12.5, color: "var(--fg-dim)" }}>Move escrow revenue into Treasury (not a partner payout).</div>
        </MetricCard>
      </div>
    </div>
  );
}

export function TreasurySummaryPage() {
  return (
    <div>
      <PageHeader
        title="Treasury Accounting"
        subtitle="Track Gao Treasury wallet, escrow state, partner liabilities, credits, and payout workflow."
        method="GET"
        path="/v2/admin/bff/treasury/summary"
      />
      <AuthRequired>
        {/* Action tiles first (§9), then the numbers.
            Every tile is NAVIGATION only and carries no money string —
            `TreasurySummaryPage.test.tsx` asserts eleven separate money
            values appear EXACTLY ONCE on this page, so a tile echoing a
            figure already in a card would throw "found multiple elements".
            The page is also asserted GET-only, which rules out any tile
            that acts rather than navigates. */}
        <div style={{ marginBottom: 22 }}>
          <TileGrid>
            <ActionTile
              to="/admin/partner-payables"
              label="Partner payables"
              description="Manage partner payments"
              icon={<IconPayables size={22} />}
            />
            <ActionTile
              to="/escrow/refunds"
              label="Refunds"
              description="Review refund requests"
              icon={<IconRefunds size={22} />}
            />
            <ActionTile
              to="/approvals"
              label="Approvals"
              description="Review pending actions"
              icon={<IconApprovals size={22} />}
            />
            <ActionTile
              to="/settlement-control"
              label="System health"
              description="Check Gao services"
              icon={<IconHealth size={22} />}
            />
          </TileGrid>
        </div>

        <TreasuryInner />
      </AuthRequired>
    </div>
  );
}
